<template>
  <div class="donut-stat-card" :class="cardClasses" @click="handleClick">
    <!-- Header -->
    <div class="card-header">
      <h3 class="card-title">{{ config.title }}</h3>
      <span v-if="config.badge" :class="badgeClasses">{{ config.badge }}</span>
    </div>

    <!-- Main Content -->
    <div class="card-content">
      <!-- Donut Chart Container -->
      <div class="chart-container">
        <div class="chart-wrapper" :style="chartWrapperStyle">
          <svg :width="chartSize" :height="chartSize" class="donut-chart">
            <!-- Background circle -->
            <circle
              :cx="center"
              :cy="center"
              :r="radius"
              fill="none"
              stroke="#F3F4F6"
              :stroke-width="strokeWidth"
            />

            <!-- Data segments -->
            <circle
              v-for="(segment, segmentIndex) in processedSegments"
              :key="`segment-${segmentIndex}`"
              :cx="center"
              :cy="center"
              :r="radius"
              fill="none"
              :stroke="segment.color"
              :stroke-width="strokeWidth"
              :stroke-dasharray="segment.dashArray"
              :stroke-dashoffset="segment.dashOffset"
              :transform="`rotate(-90 ${center} ${center})`"
              class="chart-segment"
              :class="{ 'segment-hover': hoveredSegment === segmentIndex }"
              @mouseenter="hoveredSegment = segmentIndex"
              @mouseleave="hoveredSegment = null"
            />
          </svg>

          <!-- Center Value -->
          <div class="chart-center">
            <div class="center-value">{{ centerValueDisplay }}</div>
            <div class="center-label">{{ centerLabelDisplay }}</div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div v-if="config.showLegend && config.segments" class="chart-legend">
        <div
          v-for="(segment, index) in config.segments"
          :key="`legend-${index}`"
          class="legend-item"
          :class="{ 'legend-hover': hoveredSegment === index }"
          @mouseenter="hoveredSegment = index"
          @mouseleave="hoveredSegment = null"
        >
          <div
            class="legend-color"
            :style="{ backgroundColor: segment.color }"
          ></div>
          <div class="legend-content">
            <span class="legend-label">{{ segment.label }}</span>
            <span class="legend-value">{{
              formatValue(data[segment.key])
            }}</span>
          </div>
        </div>
      </div>

      <!-- Summary Stats -->
      <div v-if="showSummary" class="summary-stats">
        <div class="summary-item">
          <span class="summary-label">Total</span>
          <span class="summary-value">{{ totalValue }}</span>
        </div>
        <div v-if="largestSegment" class="summary-item">
          <span class="summary-label">Largest</span>
          <span class="summary-value"
            >{{ largestSegment.label }} ({{ largestSegment.percentage }}%)</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DonutStatCard",
  props: {
    config: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      hoveredSegment: null
    };
  },
  computed: {
    cardClasses() {
      return [
        "bg-white",
        "rounded-xl",
        "shadow-sm",
        "border",
        "border-gray-100",
        "p-6",
        "hover:shadow-md",
        "transition-all",
        "duration-300",
        "cursor-pointer",
        "hover:border-gray-200"
      ];
    },

    badgeClasses() {
      const colorMap = {
        green: "bg-emerald-100 text-emerald-700",
        blue: "bg-blue-100 text-blue-700",
        red: "bg-red-100 text-red-700",
        yellow: "bg-yellow-100 text-yellow-700",
        purple: "bg-purple-100 text-purple-700"
      };

      return [
        "inline-flex",
        "items-center",
        "px-2.5",
        "py-0.5",
        "rounded-full",
        "text-xs",
        "font-medium",
        colorMap[this.config.badgeColor] || colorMap.blue
      ];
    },

    chartSize() {
      return this.config.size === "large"
        ? 120
        : this.config.size === "small"
        ? 80
        : 100;
    },

    center() {
      return this.chartSize / 2;
    },

    radius() {
      return (this.chartSize - this.strokeWidth * 2) / 2;
    },

    strokeWidth() {
      return this.config.strokeWidth || 12;
    },

    circumference() {
      return 2 * Math.PI * this.radius;
    },

    chartWrapperStyle() {
      return {
        width: `${this.chartSize}px`,
        height: `${this.chartSize}px`
      };
    },

    totalValue() {
      if (!this.config.segments) return 0;
      return this.config.segments.reduce((sum, segment) => {
        return sum + (this.data[segment.key] || 0);
      }, 0);
    },

    centerValueDisplay() {
      if (this.config.centerValue) {
        return this.formatValue(this.data[this.config.centerValue]);
      }
      return this.formatValue(this.totalValue);
    },

    centerLabelDisplay() {
      return this.config.centerLabel || this.config.title;
    },

    processedSegments() {
      if (!this.config.segments || this.totalValue === 0) return [];

      let cumulativePercentage = 0;

      return this.config.segments.map(segment => {
        const value = this.data[segment.key] || 0;
        const percentage = (value / this.totalValue) * 100;
        const dashArray = `${(percentage / 100) * this.circumference} ${
          this.circumference
        }`;
        const dashOffset = -((cumulativePercentage / 100) * this.circumference);

        cumulativePercentage += percentage;

        return {
          ...segment,
          value,
          percentage: percentage.toFixed(1),
          dashArray,
          dashOffset
        };
      });
    },

    largestSegment() {
      if (!this.processedSegments.length) return null;

      return this.processedSegments.reduce((largest, current) => {
        return current.value > largest.value ? current : largest;
      });
    },

    showSummary() {
      return this.config.showSummary !== false;
    }
  },
  methods: {
    formatValue(value) {
      if (typeof value === "number") {
        if (value >= 1000000) {
          return (value / 1000000).toFixed(1) + "M";
        }
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + "K";
        }
        return value.toLocaleString();
      }
      return value || "0";
    },

    handleClick() {
      this.$emit("card-click", {
        type: "donut",
        config: this.config,
        data: this.data,
        segments: this.processedSegments
      });
    }
  }
};
</script>

<style scoped>
.donut-stat-card {
  position: relative;
  overflow: hidden;
}

.donut-stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.donut-stat-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.chart-container {
  position: relative;
  display: flex;
  justify-content: center;
}

.chart-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-chart {
  transform: rotate(0deg);
  transition: transform 0.3s ease;
}

.chart-segment {
  transition: all 0.3s ease;
  cursor: pointer;
}

.chart-segment:hover,
.segment-hover {
  filter: brightness(1.1);
  stroke-width: 14;
}

.chart-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
}

.center-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.center-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.chart-legend {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 240px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.legend-item:hover,
.legend-hover {
  background-color: #f9fafb;
  transform: translateX(2px);
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.legend-label {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.legend-value {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 600;
}

.summary-stats {
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
  width: 100%;
}

.summary-item {
  flex: 1;
  text-align: center;
}

.summary-label {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 0.25rem;
}

.summary-value {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

/* Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.donut-stat-card {
  animation: slideInUp 0.4s ease-out;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .chart-legend {
    max-width: none;
  }

  .summary-stats {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
